/** @format */

import { AttributeOption, ProductSelectionState } from '@pdp/data-access/interfaces';
import { ProductPrice, SkuDefiningAttribute } from '@shared/data-access/interfaces';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  selectAllProductSkus,
  selectAllProductStyle,
  selectEcodeLevelPrice,
  selectSkusWithInventory,
} from '@pdp/data-access/state';

import { getProductPriceFromSku } from '@pdp/utils';
import { getSelectedAttributeValue } from '../utils/attribute.utils';

/**  @description Attributes State Selectors */
export const selectProductionSelectionState =
  createFeatureSelector<ProductSelectionState>('productSelection');

export const selectProductAttributes = createSelector(
  selectProductionSelectionState,
  (state) => state.attributes,
);

export const selectAttributes = createSelector(
  selectProductAttributes,
  (state) => state.allAttributeOptions,
);

export const selectSelectedAttributeOptions = createSelector(
  selectProductAttributes,
  (state) => state.selectedAttributeOptions,
);

export const selectSelectedAttributeOptionsMap = createSelector(
  selectProductAttributes,
  (state) => {
    const attributeMap: Record<string, string> = {};
    state?.selectedAttributeOptions?.forEach((option) => {
      attributeMap[option.name] = option.value;
    });
    return attributeMap;
  },
);

export const selectSelectedAttributeValue = (attributeName: string | undefined) =>
  createSelector(selectSelectedAttributeOptions, (selectedAttributeOptions) =>
    getSelectedAttributeValue(selectedAttributeOptions, attributeName),
  );

export const selectSelectedTextColorAttribute = createSelector(
  selectSelectedAttributeOptions,
  (selectedAttributeOptions) =>
    selectedAttributeOptions?.find((attribute) => attribute.name === 'Text Color')?.value,
);

export const selectSelectedColorAttribute = createSelector(
  selectSelectedAttributeOptions,
  (selectedAttributeOptions) =>
    selectedAttributeOptions?.find((attribute) => attribute.name.toLowerCase() === 'color'),
);

export const selectSelectedSizeAttribute = createSelector(
  selectSelectedAttributeOptions,
  (selectedAttributeOptions) =>
    selectedAttributeOptions?.find((attribute) => attribute.name.toLowerCase().includes('size')),
);

export const selectColorToResolvedSkuMap = createSelector(
  selectAllProductSkus,
  selectSelectedAttributeOptions,
  (allProductSkus, selectedAttributeOptions) => {
    const attributeNameToValueMap = {};
    selectedAttributeOptions.forEach((attribute) => {
      attributeNameToValueMap[attribute.name] = attribute.value;
    });

    const colorToResolvedSkuMap = {};

    allProductSkus?.forEach((sku) => {
      let skuColor = '';
      const skuHasAllSelectedAttributes = sku.definingAttributes.every((attribute) => {
        if (attribute.name === 'Color') {
          skuColor = attribute.value;
          return true;
        } else {
          return attribute.value === attributeNameToValueMap[attribute.name];
        }
      });

      if (skuHasAllSelectedAttributes) {
        colorToResolvedSkuMap[skuColor] = sku;
      }
    });

    return colorToResolvedSkuMap;
  },
);

const doesADefiningAttributeMatchSelectedValue = (definingAttributes, selectedAttribute) =>
  definingAttributes.some(
    (definingAttribute) =>
      definingAttribute.name.toLowerCase() === selectedAttribute.name.toLowerCase() &&
      definingAttribute.value.toLowerCase() === selectedAttribute.value.toLowerCase(),
  );

// Sets map of available attributes that can be selected based on the currently selected attributes
// example: {color: ["red", "blue"], size: ["small", "medium"]}
export const selectAvailableAttributes = createSelector(
  selectSkusWithInventory,
  selectSelectedAttributeOptions,
  selectAllProductStyle,
  (allSkusWithInventory, selectedAttributeOptions, productStyle) => {
    const availableAttributes: Record<string, string[]> = {};
    const definingAttributes = productStyle?.definingAttributes;
    if (!definingAttributes?.length) {
      return availableAttributes;
    }
    // loop through all defining attributes categories for this ecode, so run one time for color, one time for size, etc.
    definingAttributes.forEach((attribute) => {
      // create a list of all selected attribute values that are not the current attribute
      const tempAttributes = selectedAttributeOptions.filter(
        (attr) => attr.name.toLowerCase() !== attribute.name.toLowerCase(),
      );
      let availableSkus = [...allSkusWithInventory.skus];

      // filter out all skus that do not have the selected attributes
      tempAttributes.forEach((selectedAttribute) => {
        availableSkus = availableSkus.filter((sku) => {
          if (!sku.definingAttributes) {
            return;
          }
          return doesADefiningAttributeMatchSelectedValue(
            sku.definingAttributes,
            selectedAttribute,
          );
        });
      });

      // create a list of all available options for the current attribute, based on the selected value of the other attributes
      availableAttributes[attribute.name] = availableSkus.map((sku) => {
        if (!sku.definingAttributes) {
          return;
        }
        let attributeMatch: string;
        sku.definingAttributes.forEach((availableAttribute) => {
          if (availableAttribute.name === attribute.name) {
            attributeMatch = availableAttribute.value;
          }
        });
        return attributeMatch;
      });
    });

    return availableAttributes;
  },
);

export const selectErrorList = createSelector(selectProductAttributes, (state) => state.errorList);
export const selectIsErrorOnAttributes = createSelector(
  selectErrorList,
  (errorList) => !!errorList,
);

const someDefiningAttributeMatches = (
  defAttrs: SkuDefiningAttribute[],
  attrOption: AttributeOption,
) =>
  defAttrs?.some(
    (defAttr) => defAttr.name === attrOption.name && defAttr.value === attrOption.value,
  );

export const selectAttributePrices = (attributeName: string) =>
  createSelector(
    selectAttributes,
    selectAllProductSkus,
    selectSelectedAttributeOptions,
    selectEcodeLevelPrice,
    (allAttributes, productSkus, selectedAttributeOptions, ecodeLevelPrice) => {
      const attributePriceMap = new Map<string, ProductPrice>();

      const specifiedAttrGroup = allAttributes.find(
        (attributeGroup) => attributeGroup[0].name === attributeName,
      );

      if (!specifiedAttrGroup) {
        return attributePriceMap;
      }

      const otherSelectedAttrOptions = selectedAttributeOptions.filter(
        (selectedAttrOption) => selectedAttrOption.name !== attributeName,
      );

      for (const attrOption of specifiedAttrGroup) {
        const attrOptionPrices = productSkus?.reduce((prices: ProductPrice, currentSku) => {
          // Make sure sku matches the current attribute option & all other selected attributes
          const skuMatchesAttributes = [attrOption, ...otherSelectedAttrOptions].every(
            (selectedAttrOption) =>
              someDefiningAttributeMatches(currentSku.definingAttributes, selectedAttrOption),
          );

          if (!skuMatchesAttributes) {
            return prices;
          }

          const skuPrice = getProductPriceFromSku(currentSku.prices);

          if (!prices) {
            return skuPrice;
          }

          prices.emptyPrice ||= skuPrice.emptyPrice;

          if (skuPrice.minOfferPrice < prices.minOfferPrice) {
            prices.minOfferPrice = skuPrice.minOfferPrice;
          }
          if (skuPrice.minListPrice < prices.minListPrice) {
            prices.minListPrice = skuPrice.minListPrice;
          }
          if (skuPrice.minOfferPrice > prices.maxOfferPrice) {
            prices.maxOfferPrice = skuPrice.minOfferPrice;
          }
          if (skuPrice.minListPrice > prices.maxListPrice) {
            prices.maxListPrice = skuPrice.minListPrice;
          }

          return prices;
        }, null);

        attributePriceMap.set(attrOption.value, attrOptionPrices ?? ecodeLevelPrice);
      }

      return attributePriceMap;
    },
  );

export const selectFirstSizeAttributeName = createSelector(selectAttributes, (attributes) =>
  attributes
    .map((attribute) => attribute[0]?.name)
    .find((attributeName) => attributeName.toLowerCase().includes('size')),
);
